import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";

// Après un changement de token : suit le redémarrage du bot jusqu'à ce qu'il se
// reconnecte au site avec le nouveau token (statut « Système » du tableau de bord).
const RECENT_MS = 30 * 60 * 1000;

export default function TokenSwitchStatus({ botId, refreshKey = 0 }) {
  const [view, setView] = useState(null);
  const load = useCallback(() => axios
    .get(`${API}/dash/bots/${botId}/system`, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
    .then((r) => setView(r.data))
    .catch(() => {}), [botId]);

  useEffect(() => { setView(null); load(); }, [load, refreshKey]);
  const sw = view?.token_switch;
  const recent = sw?.at && Date.now() - new Date(sw.at).getTime() < RECENT_MS;
  const waiting = recent && sw.pending && view.status !== "error";
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [waiting, load]);

  if (!recent) return null;
  if (view.status === "error") {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="token-switch-status">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{view.error || "Le bot n'a pas pu démarrer avec le nouveau token."}</span>
      </div>
    );
  }
  if (waiting) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="token-switch-status">
        <Loader2 className="w-4 h-4 shrink-0 mt-0.5 animate-spin" />
        <span>Redémarrage avec le nouveau token… L'ancien est arrêté, le bot se connecte à Discord puis au site (en général moins d'une minute).</span>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100" data-testid="token-switch-status">
      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{view.status === "online" ? "Le bot a redémarré avec le nouveau token et il est reconnecté au site." : "Le bot s'est reconnecté au site avec le nouveau token."}</span>
    </div>
  );
}
